import { useMemo } from "react";
import type { ClientToHostMessage, MessagePayload } from "@rubyplay/effw-connector";
import { useRegisterCommands, type Command } from "@/commands/CommandRegistryContext";
import { useCommunicator } from "@/contexts/CommunicatorContext";
import { useSnapshotBridge } from "@/contexts/SnapshotBridgeContext";
import { CHEAT_COMMANDS } from "@/cheats/cheatCommandNames";
import {
	buildFatalPayload,
	getEfRuntimeInstanceId,
	hasFatalBeenReported,
	markFatalReported,
} from "@/runtime/efRuntimeProcess";
import { debugLog, debugWarn } from "@/utils/debug";
import { parseBetPayload } from "@/shared/parsers/betPayload";

type FatalCategory = MessagePayload<ClientToHostMessage, "FatalError">["category"];

type WindowWithCaptainUpTestFlags = Window & {
	__EFW_TEST_CAPTAINUP_AUTH_TIMEOUT__?: boolean;
	__EFW_TEST_CAPTAINUP_AUTH_BAD_REQUEST__?: boolean;
	__EFW_TEST_CAPTAINUP_AUTH_NOT_FOUND__?: boolean;
	__EFW_TEST_CAPTAINUP_AUTH_SERVER_ERROR__?: boolean;
	__EFW_TEST_CAPTAINUP_SDK_LOAD_FAILURE__?: boolean;
	__EFW_TEST_CAPTAINUP_SDK_INIT_FAILURE__?: boolean;
};

const EFW_CHEAT_BET_UPDATED_EVENT = "efw:cheat:bet-updated";

function parseFatalCategory(value: unknown): FatalCategory {
	if (value === "RUNTIME" || value === "UNHANDLED_REJECTION") {
		return value;
	}

	return "RUNTIME";
}

function parseFatalMessage(value: unknown): string {
	if (typeof value === "string" && value.trim().length > 0) {
		return value.trim();
	}

	return "[Cheats] forced fatal error";
}

function buildCheatFatalPayload(payload?: unknown): MessagePayload<ClientToHostMessage, "FatalError"> {
	if (payload && typeof payload === "object") {
		const candidate = payload as {
			category?: unknown;
			message?: unknown;
			stack?: unknown;
		};

		const base = buildFatalPayload(
			parseFatalCategory(candidate.category),
			new Error(parseFatalMessage(candidate.message))
		);

		return {
			...base,
			stack:
				typeof candidate.stack === "string" && candidate.stack.trim().length > 0
					? candidate.stack
					: base.stack,
		};
	}

	return buildFatalPayload("RUNTIME", new Error("[Cheats] forced fatal error"));
}

export function EFProtocolCheatCommands() {
	const { sendHeartBeat, sendFatalError, sendClientReady } = useCommunicator();
	const { triggerAllHitAreaWidgetSnapshots } = useSnapshotBridge();

	const commands = useMemo<Command[]>(() => {
		return [
			{
				name: CHEAT_COMMANDS.ef.sendHeartBeat,
				execute: async () => {
					const payload: MessagePayload<ClientToHostMessage, "HeartBeat"> = {
						instanceId: getEfRuntimeInstanceId(),
						timestamp: Date.now(),
						state: "healthy",
					};

					debugLog("[Cheats] sendHeartBeat", payload);

					sendHeartBeat(payload);
				},
			},
			{
				name: CHEAT_COMMANDS.ef.sendClientReady,
				execute: async () => {
					const payload: MessagePayload<ClientToHostMessage, "ClientReady"> = {
						instanceId: getEfRuntimeInstanceId(),
					};

					debugLog("[Cheats] sendClientReady", payload);

					sendClientReady(payload);
				},
			},
			{
				name: CHEAT_COMMANDS.ef.sendFatalError,
				execute: async (payload?: unknown) => {
					if (hasFatalBeenReported()) {
						debugWarn("[Cheats] sendFatalError ignored because fatal was already reported");
						return;
					}

					const fatalPayload = buildCheatFatalPayload(payload);

					markFatalReported();

					debugLog("[Cheats] sendFatalError", fatalPayload);

					sendFatalError(fatalPayload);
				},
			},
			{
				name: CHEAT_COMMANDS.ef.sendBetUpdated,
				execute: async (payload?: unknown) => {
					const parsed = parseBetPayload(payload);

					if (!parsed) {
						debugWarn("[Cheats] sendBetUpdated ignored because payload is invalid", {
							payload,
						});
						return;
					}

					debugLog("[Cheats] sendBetUpdated", parsed);

					window.dispatchEvent(
						new CustomEvent(EFW_CHEAT_BET_UPDATED_EVENT, {
							detail: parsed,
						})
					);
				},
			},
			{
				name: CHEAT_COMMANDS.ef.triggerCaptainUpAuthTimeout,
				execute: async () => {
					if (typeof window === "undefined") {
						return;
					}

					const targetWindow = window as WindowWithCaptainUpTestFlags;
					targetWindow.__EFW_TEST_CAPTAINUP_AUTH_TIMEOUT__ = true;

					debugLog("[Cheats] triggerCaptainUpAuthTimeout armed");
				},
			},
			{
				name: CHEAT_COMMANDS.ef.triggerCaptainUpAuthBadRequest,
				execute: async () => {
					if (typeof window === "undefined") {
						return;
					}

					const targetWindow = window as WindowWithCaptainUpTestFlags;
					targetWindow.__EFW_TEST_CAPTAINUP_AUTH_BAD_REQUEST__ = true;

					debugLog("[Cheats] triggerCaptainUpAuthBadRequest armed");
				},
			},
			{
				name: CHEAT_COMMANDS.ef.triggerCaptainUpAuthNotFound,
				execute: async () => {
					if (typeof window === "undefined") {
						return;
					}

					const targetWindow = window as WindowWithCaptainUpTestFlags;
					targetWindow.__EFW_TEST_CAPTAINUP_AUTH_NOT_FOUND__ = true;

					debugLog("[Cheats] triggerCaptainUpAuthNotFound armed");
				},
			},
			{
				name: CHEAT_COMMANDS.ef.triggerCaptainUpAuthServerError,
				execute: async () => {
					if (typeof window === "undefined") {
						return;
					}

					const targetWindow = window as WindowWithCaptainUpTestFlags;
					targetWindow.__EFW_TEST_CAPTAINUP_AUTH_SERVER_ERROR__ = true;

					debugLog("[Cheats] triggerCaptainUpAuthServerError armed");
				},
			},
			{
				name: CHEAT_COMMANDS.ef.triggerCaptainUpSdkLoadFailure,
				execute: async () => {
					if (typeof window === "undefined") {
						return;
					}

					const targetWindow = window as WindowWithCaptainUpTestFlags;
					targetWindow.__EFW_TEST_CAPTAINUP_SDK_LOAD_FAILURE__ = true;

					debugLog("[Cheats] triggerCaptainUpSdkLoadFailure armed");
				},
			},
			{
				name: CHEAT_COMMANDS.ef.triggerCaptainUpSdkInitFailure,
				execute: async () => {
					if (typeof window === "undefined") {
						return;
					}

					const targetWindow = window as WindowWithCaptainUpTestFlags;
					targetWindow.__EFW_TEST_CAPTAINUP_SDK_INIT_FAILURE__ = true;

					debugLog("[Cheats] triggerCaptainUpSdkInitFailure armed");
				},
			},
			{
				name: CHEAT_COMMANDS.ef.triggerAllHitAreaWidgetSnapshots,
				execute: async () => {
					debugLog("[Cheats] triggerAllHitAreaWidgetSnapshots");
					triggerAllHitAreaWidgetSnapshots();
				},
			},
		];
	}, [sendClientReady, sendFatalError, sendHeartBeat, triggerAllHitAreaWidgetSnapshots]);

	useRegisterCommands(commands);

	return null;
}

export default EFProtocolCheatCommands;